import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip,
  CircularProgress, Alert, Stack, useMediaQuery, useTheme
} from '@mui/material';
import { getTicketNotes } from '../../services/supportTicketApi';

/**
 * One of the user's own support tickets: what they reported and every reply staff
 * made public (7 Oct 2026). Opened from the notification bell. Internal staff notes
 * never reach this view: the server returns public notes only.
 */

const STATUS_LABEL = {
  open: { label: 'Open', color: 'default' },
  in_progress: { label: 'Being looked at', color: 'info' },
  waiting_on_user: { label: 'Waiting for you', color: 'warning' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' }
};

const formatWhen = (d) => (d ? new Date(d).toLocaleString(undefined, {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
}) : '');

const TicketUpdatesDialog = ({ ticketId, token, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !ticketId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setTicket(null);
    getTicketNotes(ticketId, token)
      .then(data => { if (!cancelled) setTicket(data); })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.status === 404
          ? 'This report could not be found. It may have been removed.'
          : 'Could not load this report. Check your connection and try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ticketId, token]);

  const status = ticket && (STATUS_LABEL[ticket.status] || { label: ticket.status, color: 'default' });
  const replies = ticket?.notes || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>Your report {ticket?.ticketNumber || ''}</span>
        {status && <Chip size="small" label={status.label} color={status.color} />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {ticket && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                You reported{ticket.createdAt ? ` on ${formatWhen(ticket.createdAt)}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                {ticket.description}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {replies.length ? 'Replies' : 'No replies yet'}
              </Typography>
              <Stack spacing={1.5}>
                {replies.map((note, i) => (
                  <Box key={`${note.createdAt}-${i}`} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.text}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[note.authorName, formatWhen(note.createdAt)].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketUpdatesDialog;
